import { useEffect, useRef, useState } from "react";
import { api, API_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Send, Loader2, Sparkles } from "lucide-react";

const GREETING = "Olá! Sou o consultor virtual da ATP Confecções. 👋\nMe conta: pra quem é o uniforme e o que você tem em mente? Posso te ajudar a escolher tecido, técnica e até preparar uma amostra visual antes de passar para um vendedor.";

export function getChatSessionId() {
  let sid = localStorage.getItem("atp_chat_session");
  if (!sid) {
    sid = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2));
    localStorage.setItem("atp_chat_session", sid);
  }
  return sid;
}

export default function ConsultantChat({ compact = false, initialText = "" }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState(initialText);
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [ready, setReady] = useState(false);
  const listRef = useRef(null);
  const sessionId = getChatSessionId();

  useEffect(() => {
    api.get(`/ai/consultant/${sessionId}/history`).then(({ data }) => {
      const hist = data.messages.map((m) => ({ role: m.direction === "in" ? "user" : "assistant", text: m.text, image_id: m.image_id }));
      setMessages(hist.length ? hist : [{ role: "assistant", text: GREETING }]);
      setLeadDone(Boolean(data.lead_id));
    }).catch(() => setMessages([{ role: "assistant", text: GREETING }])).finally(() => setReady(true));
  }, [sessionId]);

  useEffect(() => { if (initialText) setText(initialText); }, [initialText]);
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const pollMockup = (id) => {
    let tries = 0;
    const iv = setInterval(async () => {
      tries++;
      try {
        const { data } = await api.get(`/mockups/${id}/meta`);
        if (data.status === "ready") {
          clearInterval(iv);
          setMessages((m) => m.map((x) => (x.image_id === id ? { ...x, pending: false } : x)));
        } else if (data.status === "failed") {
          clearInterval(iv);
          setMessages((m) => m.map((x) => (x.image_id === id ? { ...x, pending: false, failed: true } : x)));
        }
      } catch (e) { /* keep trying */ }
      if (tries > 40) clearInterval(iv);
    }, 2000);
  };

  const send = async () => {
    const q = text.trim();
    if (!q || loading) return;
    setText("");
    setLoading(true);
    setStreaming(true);
    let asstIdx = -1;
    setMessages((m) => { asstIdx = m.length + 1; return [...m, { role: "user", text: q }, { role: "assistant", text: "", streaming: true }]; });
    try {
      const res = await fetch(`${API_URL}/ai/consultant/stream`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: q }),
      });
      if (!res.ok || !res.body) throw new Error("stream unavailable");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          let ev;
          try { ev = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (ev.type === "text") {
            setMessages((m) => { const c = [...m]; if (c[asstIdx]) c[asstIdx] = { ...c[asstIdx], text: (c[asstIdx].text || "") + ev.delta }; return c; });
          } else if (ev.type === "mockup") {
            setMessages((m) => { const c = [...m]; c.splice(asstIdx + 1, 0, { role: "assistant", image_id: ev.id, pending: true }); return c; });
            pollMockup(ev.id);
          } else if (ev.type === "lead") {
            setLeadDone(true);
          } else if (ev.type === "error") {
            setMessages((m) => { const c = [...m]; if (c[asstIdx]) c[asstIdx] = { ...c[asstIdx], text: ev.error, error: true, streaming: false }; return c; });
          } else if (ev.type === "done") {
            setMessages((m) => { const c = [...m]; if (c[asstIdx]) c[asstIdx] = { ...c[asstIdx], text: ev.reply || c[asstIdx].text, streaming: false }; return c; });
          }
        }
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; if (c[asstIdx]) c[asstIdx] = { ...c[asstIdx], text: "Tive um problema para responder agora. Pode tentar de novo?", error: true, streaming: false }; return c; });
    } finally { setLoading(false); setStreaming(false); }
  };

  const visible = messages.filter((m) => m.image_id || (m.text && m.text.length) || m.streaming);

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="consultant-chat">
      <div ref={listRef} className={`flex-1 overflow-y-auto ${compact ? "p-3 space-y-2.5" : "p-4 sm:p-6 space-y-3"}`} data-testid="consultant-messages">
        {!ready && <div className="text-xs text-slate-500">Carregando...</div>}
        {visible.map((m, i) => <Bubble key={i} m={m} i={i} compact={compact} />)}
        {loading && !streaming && (
          <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="consultant-typing">
            <Loader2 size={14} className="animate-spin text-[#0CDBF2]" /> Consultor está digitando...
          </div>
        )}
        {leadDone && (
          <div className="text-[11px] text-[#0CDBF2] bg-[#0CDBF2]/10 border border-[#0CDBF2]/30 rounded-md px-3 py-2" data-testid="consultant-lead-badge">
            Pedido registrado — um vendedor da ATP vai retornar pelo WhatsApp.
          </div>
        )}
      </div>
      <div className={`border-t border-[#1E293B] flex gap-2 ${compact ? "p-2.5" : "p-3 sm:p-4"}`}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Escreva sua mensagem..."
          rows={1}
          data-testid="consultant-input"
          className="flex-1 resize-none bg-[#07090E] border border-[#1E293B] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#0CDBF2]/60 max-h-32"
        />
        <Button onClick={send} disabled={loading || !text.trim()} data-testid="consultant-send"
          className="bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] h-auto px-4">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </Button>
      </div>
    </div>
  );
}

function Bubble({ m, i, compact }) {
  const user = m.role === "user";
  if (m.image_id) {
    return (
      <div className="mr-6" data-testid={`consultant-msg-mockup-${i}`}>
        <div className="inline-flex items-center gap-1.5 text-[11px] text-[#0CDBF2] mb-1.5"><Sparkles size={12} /> Amostra de mockup</div>
        {m.pending ? (
          <div className={`rounded-xl border border-[#1E293B] bg-[#111622] flex items-center justify-center text-xs text-slate-400 ${compact ? "w-[240px] h-[180px]" : "max-w-sm w-full h-64"}`} data-testid={`consultant-mockup-pending-${i}`}>
            <Loader2 size={14} className="animate-spin mr-2 text-[#0CDBF2]" /> Gerando amostra…
          </div>
        ) : m.failed ? (
          <div className="text-xs text-slate-400">Não consegui gerar a amostra agora; o vendedor enviará opções.</div>
        ) : (
          <img src={`${API_URL}/mockups/${m.image_id}`} alt="Mockup do uniforme"
            className={`rounded-xl border border-[#1E293B] ${compact ? "max-w-[240px]" : "max-w-sm"} w-full`} />
        )}
      </div>
    );
  }
  return (
    <div data-testid={`consultant-msg-${m.role}-${i}`}
      className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed max-w-[85%] ${
        user ? "bg-[#0CDBF2] text-[#07090E] ml-auto rounded-br-md"
             : m.error ? "bg-red-500/10 border border-red-500/30 text-red-200 rounded-bl-md"
             : "bg-[#111622] border border-[#1E293B] text-slate-100 rounded-bl-md"}`}>
      {m.text}{m.streaming && <span className="inline-block w-1.5 h-3.5 ml-0.5 bg-[#0CDBF2] align-middle animate-pulse" />}
    </div>
  );
}
